import { mdiAlertCircleOutline, mdiCancel, mdiCheckCircleOutline } from '@mdi/js'
import { Icon } from '@mdi/react'
import { memo, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { NETWORKS_NAMES } from 'shared/constants'
import styles from './StatusBar.pcss'

export interface StatusBarProps {
  isReady: boolean
  isOptimized: boolean
  isEmpty: boolean
  isInEditMode: boolean
  emptyNetworks: string[]
  networksCount: number
}

export const StatusBar = memo(function StatusBar(props: StatusBarProps) {
  const { isReady, isOptimized, isEmpty, isInEditMode, emptyNetworks, networksCount } = props

  const emptyPostsMessage = useMemo(() => {
    if (isEmpty) {
      return null
    }
    const list = emptyNetworks.map(network => NETWORKS_NAMES[network] || network)
    if (list.length === 0 || list.length === networksCount) {
      return null
    }
    if (list.length === 1) {
      return `Your ${list[0]} post is empty!`
    }
    if (list.length === 2) {
      return `Your ${list[0]} and ${list[1]} posts are empty!`
    }
    const last = list[list.length - 1]
    return `Your ${list.slice(0, -1).join(', ')} and ${last} posts are empty!`
  }, [isEmpty, emptyNetworks, networksCount])

  const statusBarClassName = useMemo(() => {
    let className = isReady ? styles.green : styles.red
    if (isReady && !isOptimized) {
      className = styles.yellow
    }
    if (emptyPostsMessage === null && isEmpty) {
      className = styles.transparent
    }
    return `${styles['status-bar']} ${className}`
  }, [emptyPostsMessage, isEmpty, isOptimized, isReady])

  return (
    <div className={styles.state}>
      <div className={statusBarClassName}>
        {isReady && isOptimized && (
          <Icon path={mdiCheckCircleOutline} />
        )}
        {!isReady && (
          <Icon path={mdiCancel} />
        )}
        {isReady && !isOptimized && (
          <Icon path={mdiAlertCircleOutline} />
        )}
      </div>
      <div className={`${styles['message-box']} ${isEmpty ? styles.hidden : ''}`}>
        <p className={styles['msg-status']}>
          {isReady && isOptimized && (
            <FormattedMessage id={isInEditMode ? 'composer.labels.post-edit-ready' : 'composer.labels.post-ready'} />
          )}
          {isReady && !isOptimized && (
            <FormattedMessage
              id={isInEditMode ? 'composer.labels.post-edit-not-optimized' : 'composer.labels.post-not-optimized'}
            />
          )}
          {!isReady && !emptyPostsMessage && (
            <FormattedMessage id={isInEditMode ? 'composer.labels.post-edit-not-ready' : 'composer.labels.post-not-ready'} />
          )}
          {emptyPostsMessage}
        </p>
      </div>
    </div>
  )
})

export default StatusBar
